import * as React from 'react';

import {Routing} from 'nutshell-core/routing';

import {colors} from 'shells/colors/colors';
import {Link} from 'shells/link/link';
import {LoadingPage} from 'shells/loading-page/loading-page';
import {Footer} from 'shells/summary/footer';
import {Body} from 'shells/typography/body';

import type {EntitySummaryRating} from 'nutshell-graphql-types';
import type {GetActivityForModal_activity_tasks as ActivityTask} from 'nutshell-graphql-types';

import {FollowUpTasksSection} from './follow-up-tasks-section';

import {
    TranscriptSummaryGenerating,
    TranscriptSummaryFeatureGated,
    TranscriptSummaryConnectService,
    TranscriptSummaryAiSettings,
} from '.';

import styles from './transcript-summary.module.css';

type Props = {
    activityId: string;
    summary: string | null | undefined;
    canViewSummaryDetails: boolean;
    summaryNextAction: string;
    summaryState: string;
    isDeletingSummary: boolean;
    isGeneratingSummary: boolean;
    isNotetakerDisabled: boolean;
    onDelete: () => void | null | undefined;
    userRating: EntitySummaryRating | null | undefined;
    onRate: (rating: EntitySummaryRating) => Promise<any>;
    isSavingRating: boolean;
    onRefresh?: () => Promise<any>;
    tasks?: ActivityTask[];
};

export function TranscriptSummaryContent(props: Props) {
    let innerContent = null;
    if (props.isDeletingSummary) {
        innerContent = <LoadingPage fill={colors.plumLt} padding={16} />;
    } else if (props.isNotetakerDisabled) {
        innerContent =
            props.summaryNextAction === 'AI_SETTINGS' ? (
                <TranscriptSummaryAiSettings copy={props.summaryState} />
            ) : (
                <Body size='small'>{props.summaryState}</Body>
            );
    } else if (props.isGeneratingSummary && props.canViewSummaryDetails) {
        innerContent = <TranscriptSummaryGenerating />;
    } else if (props.summary && props.canViewSummaryDetails) {
        innerContent = (
            <Body size='small' maintainLineBreaks={true}>
                {props.summary}
            </Body>
        );
    } else if (props.summaryNextAction === 'UPGRADE') {
        innerContent = <TranscriptSummaryFeatureGated copy={props.summaryState} />;
    } else if (
        props.summaryNextAction === 'CONNECT_ZOOM' ||
        props.summaryNextAction === 'CONNECT_GOOGLE'
    ) {
        innerContent = (
            <TranscriptSummaryConnectService
                service={props.summaryNextAction}
                copy={props.summaryState}
            />
        );
    } else if (props.summaryNextAction === 'SUMMARIZE') {
        innerContent = <Body size='small'>{props.summaryState}</Body>;
    } else if (props.summaryNextAction === 'AI_SETTINGS') {
        innerContent = <TranscriptSummaryAiSettings copy={props.summaryState} />;
    } else {
        innerContent = <Body size='small'>{props.summaryState}</Body>;
    }

    return (
        <div className={styles['summary-content-container']}>
            {innerContent}
            {NutClientConfig.hasAiFollowUpTasksFF &&
                props.tasks &&
                props.tasks.length > 0 &&
                props.activityId && <FollowUpTasksSection tasks={props.tasks} />}
            {NutClientConfig.hasAiDebugging && props.activityId && (
                <div className='pt-8'>
                    <Link
                        variant='button-secondary'
                        href={`/ai/task-generation?${Routing.param({
                            id: props.activityId,
                        })}`}
                        newTab={true}
                    >
                        🏗️ Task generation playground
                    </Link>
                </div>
            )}
            {props.summary && props.canViewSummaryDetails && !props.isNotetakerDisabled && (
                <Footer
                    userRating={props.userRating}
                    onRate={props.onRate}
                    isSavingRating={props.isSavingRating}
                    onRefresh={props.onRefresh}
                    onDelete={props.onDelete ? props.onDelete : undefined}
                />
            )}
        </div>
    );
}
